import { useState, useEffect, useRef } from "react";
import { BsStars } from "react-icons/bs";
import { IoMdArrowRoundBack, IoMdAddCircle } from "react-icons/io";
import { FaRocket, FaPaperPlane, FaCheck, FaFileAlt, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import "./ServiceCreateForm.css";

// ─────────────────────────────────────────────
// File-type restriction — PDF, Excel, Word, ODT only.
// Keep in sync with ALLOWED_DOC_TYPES on the backend (ResourceService.java).
// ─────────────────────────────────────────────
const EXT_TO_DOC_TYPE = {
  pdf: "PDF",
  xlsx: "EXCEL", xls: "EXCEL", csv: "EXCEL",
  doc: "AGREEMENT", docx: "AGREEMENT",
  odt: "ODT",
};
const ALLOWED_EXTENSIONS = Object.keys(EXT_TO_DOC_TYPE);
const ACCEPT_ATTR = ALLOWED_EXTENSIONS.map((e) => `.${e}`).join(",");

const DOC_TYPE_BADGE_LABEL = {
  PDF: "PDF",
  EXCEL: "EXCEL",
  AGREEMENT: "WORD",
  ODT: "ODT",
  OTHER: "FILE",
};

const getExt = (name) => (name || "").split(".").pop()?.toLowerCase() || "";
const isAllowedFile = (file) => ALLOWED_EXTENSIONS.includes(getExt(file?.name));
const docTypeFromFile = (file) => EXT_TO_DOC_TYPE[getExt(file?.name)] || "OTHER";
const badgeLabel = (docType) => DOC_TYPE_BADGE_LABEL[docType] || "FILE";

// Splits picked files into accepted / rejected, toasts a warning for rejects.
function filterAllowedFiles(fileList) {
  const incoming = Array.from(fileList || []);
  const accepted = incoming.filter(isAllowedFile);
  const rejectedNames = incoming.filter((f) => !isAllowedFile(f)).map((f) => f.name);
  if (rejectedNames.length > 0) {
    toast.warning(`Only PDF, Excel, Word, and ODT files are allowed. Rejected: ${rejectedNames.join(", ")}`);
  }
  return accepted;
}

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─────────────────────────────────────────────
// Shared: multi-file picker with preview list + remove
// `existingFiles` is [{fileName, docType}], matching the API's resourceFiles shape.
// ─────────────────────────────────────────────
function MultiFilePicker({ files, existingFiles, onFilesChange, onRemoveExisting }) {
  const inputRef = useRef(null);

  const handlePick = (e) => {
    const accepted = filterAllowedFiles(e.target.files);
    if (accepted.length > 0) onFilesChange([...(files || []), ...accepted]);
    e.target.value = "";
  };

  const removeNewFile = (idx) => {
    const next = [...files];
    next.splice(idx, 1);
    onFilesChange(next);
  };

  const hasAnyFiles = (files?.length > 0) || (existingFiles?.length > 0);

  return (
    <div>
      <div
        style={{ border: "2px dashed #d1d5db", borderRadius: "12px", padding: "1.5rem", textAlign: "center", background: "#f9fafb", cursor: "pointer" }}
        onClick={() => inputRef.current?.click()}
      >
        <input ref={inputRef} type="file" multiple accept={ACCEPT_ATTR} style={{ display: "none" }} onChange={handlePick} />
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
          <FaCheck size={22} color={hasAnyFiles ? "#10b981" : "#9ca3af"} />
          <span style={{ fontWeight: 600 }}>{hasAnyFiles ? "Click to add more files" : "Click to select file(s)"}</span>
          <span style={{ fontSize: "0.85rem", color: "#6b7280" }}>PDF, Excel, Word, or ODT only (Max 100MB each)</span>
        </div>
      </div>

      {existingFiles?.length > 0 && (
        <div style={{ marginTop: "0.75rem", display: "flex", flexDirection: "column", gap: "6px" }}>
          {existingFiles.map((item, idx) => (
            <div key={`existing-${idx}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#f3f4f6", borderRadius: "8px", padding: "8px 12px", fontSize: "0.85rem" }}>
              <span style={{ display: "flex", alignItems: "center", gap: "8px", overflow: "hidden" }}>
                <FaFileAlt size={14} color="#6b7280" />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.fileName}</span>
                {item.docType && (
                  <span style={{ background: "#e5e7eb", color: "#374151", fontSize: "0.7rem", fontWeight: 700, padding: "2px 6px", borderRadius: "4px", letterSpacing: "0.02em" }}>
                    {badgeLabel(item.docType)}
                  </span>
                )}
                <span style={{ color: "#9ca3af", fontSize: "0.75rem" }}>(saved)</span>
              </span>
              {onRemoveExisting && (
                <button type="button" onClick={() => onRemoveExisting(idx)} style={{ background: "transparent", border: "none", cursor: "pointer", color: "#ef4444", display: "flex" }} aria-label={`Remove ${item.fileName}`}>
                  <FaTrash size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {files?.length > 0 && (
        <div style={{ marginTop: "0.75rem", display: "flex", flexDirection: "column", gap: "6px" }}>
          {files.map((file, idx) => (
            <div key={`new-${idx}-${file.name}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#ecfdf5", border: "1px solid #6ee7b7", borderRadius: "8px", padding: "8px 12px", fontSize: "0.85rem" }}>
              <span style={{ display: "flex", alignItems: "center", gap: "8px", overflow: "hidden" }}>
                <FaFileAlt size={14} color="#059669" />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 600 }}>{file.name}</span>
                <span style={{ background: "#d1fae5", color: "#065f46", fontSize: "0.7rem", fontWeight: 700, padding: "2px 6px", borderRadius: "4px", letterSpacing: "0.02em" }}>
                  {badgeLabel(docTypeFromFile(file))}
                </span>
                <span style={{ color: "#6b7280", fontSize: "0.75rem" }}>{formatBytes(file.size)}</span>
              </span>
              <button type="button" onClick={() => removeNewFile(idx)} style={{ background: "transparent", border: "none", cursor: "pointer", color: "#ef4444", display: "flex" }} aria-label={`Remove ${file.name}`}>
                <FaTrash size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// EDIT MODE (resource)
// ─────────────────────────────────────────────
function EditForm({ state, updateState, createResource, t }) {
  const handleResourceFilesChange = (files) => updateState("resourceFiles", files);
  const handleRemoveExistingFile = (idx) => {
    const next = [...(state.existingResourceFiles || [])];
    next.splice(idx, 1);
    updateState("existingResourceFiles", next);
  };

  return (
    <div className="scf-root">
      <div className="scf-header">
        <button type="button" className="scf-back" onClick={() => { updateState("menuType", ""); updateState("serviceId", null); }}>
          <IoMdArrowRoundBack size={18} />
        </button>
        <div className="scf-header-text">
          <h2>{t("Edit Resource")}</h2>
          <p>{t("Update your digital resource details below")}</p>
        </div>
      </div>

      <div className="scf-divider" />

      <form onSubmit={(e) => createResource(e, state, true)} className="scf-form">
        <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem", maxWidth: "800px" }}>
          <div className="scf-field">
            <label className="scf-label">Resource Title *</label>
            <input className="scf-input" type="text" value={state.title || ""} required onChange={(e) => updateState("title", e.target.value)} placeholder="e.g., The Ultimate 2026 Playbook" />
          </div>
          <div className="scf-field">
            <label className="scf-label">Price (₹) *</label>
            <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
              <div className="scf-amount-wrap" style={{ maxWidth: "200px" }}>
                <span className="scf-currency">₹</span>
                <input className="scf-input scf-amount-input" type="number" placeholder="0" value={state.prize ?? ""} onChange={(e) => updateState("prize", e.target.value)} />
              </div>
              <span style={{ fontSize: "0.9rem", color: "#6b7280", fontWeight: 500 }}>
                {Number(state.prize) === 0 || !state.prize ? "🎁 Offered for FREE" : "💳 Paid resource"}
              </span>
            </div>
          </div>
          <div className="scf-field">
            <label className="scf-label">Description *</label>
            <textarea className="scf-textarea" rows={5} value={state.description || ""} onChange={(e) => updateState("description", e.target.value)} placeholder="Explain what they get when they download this..." required />
          </div>
          <div className="scf-field">
            <label className="scf-label">Digital Resource File(s) *</label>
            <MultiFilePicker
              files={state.resourceFiles || []}
              existingFiles={state.existingResourceFiles || []}
              onFilesChange={handleResourceFilesChange}
              onRemoveExisting={handleRemoveExistingFile}
            />
          </div>
        </div>

        <div className="scf-submit-row">
          <button type="submit" className="scf-btn-submit">{t("Update")} Resource ✓</button>
        </div>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────
// CREATE MODE (resource — conversational UI)
// ─────────────────────────────────────────────
function CreateForm({ state, updateState, createResource, t }) {
  const [messages, setMessages] = useState([]);
  const [phase, setPhase] = useState("idle");
  const [typing, setTyping] = useState(false);
  const [textVal, setTextVal] = useState("");

  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, typing]);

  useEffect(() => {
    botSay("👋 Hi! Let's add your new digital resource. I'll guide you step by step.", () => ask("serviceName"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function addMsg(msg) { setMessages((prev) => [...prev, { id: Date.now() + Math.random(), ...msg }]); }
  function botSay(text, cb, delay = 650) {
    setTyping(true);
    setTimeout(() => { setTyping(false); addMsg({ from: "bot", text }); cb && setTimeout(cb, 180); }, delay);
  }
  function userSay(text) { addMsg({ from: "user", text }); }

  function ask(nextPhase, delay = 450) {
    const Q = {
      serviceName: "What's the title of your digital resource?",
      amount: "What's the price? (Enter 0 if it's free)",
      description: "Write a short description of what they get.",
      resourceFile: "Please upload your digital resource file(s) — PDF, Excel, Word, or ODT. You can select multiple.",
      review: "Everything's set! Review your resource and launch it. 🚀",
    };
    setTyping(true);
    setTimeout(() => { setTyping(false); addMsg({ from: "bot", text: Q[nextPhase] || "" }); setPhase(nextPhase); setTimeout(() => inputRef.current?.focus(), 100); }, delay);
  }

  const handleServiceName = () => {
    const v = textVal.trim();
    if (!v || v.length < 2) { toast.warn("Please enter a valid name."); return; }
    updateState("title", v); userSay(v); setTextVal("");
    botSay(`"${v}" — great name! 💡`, () => ask("amount"));
  };
  const handleAmount = () => {
    const v = textVal.trim();
    if (!v || isNaN(v)) { toast.warn("Please enter a valid amount."); return; }
    updateState("prize", v);
    userSay(Number(v) === 0 ? "Free" : `₹${v}`);
    setTextVal("");
    botSay("Got it.", () => ask("description"));
  };
  const handleDescription = () => {
    const v = textVal.trim();
    if (!v || v.length < 10) { toast.warn("Please enter at least 10 characters."); return; }
    updateState("description", v);
    userSay(v.length > 80 ? v.slice(0, 80) + "…" : v);
    setTextVal("");
    botSay("Perfect! Almost done.", () => ask("resourceFile"));
  };

  const handleResourceUpload = (fileList) => {
    const accepted = filterAllowedFiles(fileList);
    if (accepted.length === 0) return;
    const next = [...(state.resourceFiles || []), ...accepted];
    updateState("resourceFiles", next);
    userSay(accepted.length === 1 ? `Uploaded: ${accepted[0].name}` : `Uploaded ${accepted.length} files`);
    botSay("File(s) attached successfully! 🎉", () => { setPhase("review"); ask("review"); });
  };

  const removeReviewFile = (idx) => {
    const next = [...(state.resourceFiles || [])];
    next.splice(idx, 1);
    updateState("resourceFiles", next);
  };

  const handleTextSubmit = (e) => {
    e?.preventDefault();
    if (phase === "serviceName") handleServiceName();
    else if (phase === "amount") handleAmount();
    else if (phase === "description") handleDescription();
  };

  const renderReview = () => (
    <div className="conv-review-card">
      <div className="conv-review-grid">
        <div className="conv-review-item">
          <span className="conv-review-key">Resource Title</span>
          <input className="conv-review-edit-input" value={state.title || ""} onChange={(e) => updateState("title", e.target.value)} placeholder="Resource title" />
        </div>
        <div className="conv-review-item">
          <span className="conv-review-key">Price (₹)</span>
          <div style={{ position: "relative" }}>
            <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#15803d", fontWeight: 700, fontSize: "0.95rem" }}>₹</span>
            <input className="conv-review-edit-input" type="number" style={{ paddingLeft: 28 }} value={state.prize || ""} onChange={(e) => updateState("prize", e.target.value)} placeholder="0" />
          </div>
        </div>

        <div className="conv-review-item conv-review-item--full">
          <span className="conv-review-key">Description</span>
          <textarea className="conv-review-edit-textarea" rows={4} value={state.description || ""} onChange={(e) => updateState("description", e.target.value)} placeholder="Resource description…" />
        </div>

        <div className="conv-review-item conv-review-item--full">
          <span className="conv-review-key">Attached File(s)</span>
          <div style={{ marginTop: "5px" }}>
            {(state.resourceFiles || []).length === 0 ? (
              <span style={{ color: "#9ca3af", fontSize: "0.9rem" }}>No files attached</span>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "10px" }}>
                {state.resourceFiles.map((file, idx) => (
                  <div key={`${file.name}-${idx}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#ecfdf5", border: "1px solid #6ee7b7", borderRadius: "8px", padding: "8px 12px", fontSize: "0.85rem" }}>
                    <span style={{ display: "flex", alignItems: "center", gap: "8px", overflow: "hidden" }}>
                      <FaFileAlt size={13} color="#059669" />
                      <span style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</span>
                      <span style={{ background: "#d1fae5", color: "#065f46", fontSize: "0.7rem", fontWeight: 700, padding: "2px 6px", borderRadius: "4px", letterSpacing: "0.02em" }}>
                        {badgeLabel(docTypeFromFile(file))}
                      </span>
                      <span style={{ color: "#6b7280", fontSize: "0.75rem" }}>{formatBytes(file.size)}</span>
                    </span>
                    <button type="button" onClick={() => removeReviewFile(idx)} style={{ background: "transparent", border: "none", cursor: "pointer", color: "#ef4444", display: "flex" }} aria-label={`Remove ${file.name}`}>
                      <FaTrash size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <label style={{ cursor: "pointer", background: "#f3f4f6", padding: "6px 12px", borderRadius: "8px", fontSize: "0.85rem", fontWeight: 600, display: "inline-block" }}>
              {(state.resourceFiles || []).length > 0 ? "Add more files" : "Select files"}
              <input type="file" multiple accept={ACCEPT_ATTR} style={{ display: "none" }} onChange={(e) => { handleResourceUpload(e.target.files); e.target.value = ""; }} />
            </label>
          </div>
        </div>
      </div>

      <button type="button" className="conv-launch-btn" onClick={(e) => createResource(e, state, true)}>
        <FaRocket size={15} /> {t("Launch Resource")}
      </button>
    </div>
  );

  const renderInput = () => {
    if (phase === "review" || phase === "idle") return null;

    if (phase === "resourceFile") {
      return (
        <div className="conv-widget" style={{ width: "100%", textAlign: "center" }}>
          <div style={{ border: "2px dashed #d1d5db", borderRadius: "12px", padding: "2rem", background: "#f9fafb" }}>
            <input type="file" id="chat-file-upload" multiple accept={ACCEPT_ATTR} style={{ display: "none" }} onChange={(e) => { handleResourceUpload(e.target.files); e.target.value = ""; }} />
            <label htmlFor="chat-file-upload" style={{ cursor: "pointer", display: "flex", flexDirection: "column", gap: "10px", alignItems: "center", margin: 0 }}>
              <span style={{ background: "var(--svc-brand-light)", color: "var(--svc-brand)", padding: "12px", borderRadius: "50%" }}>
                <IoMdAddCircle size={24} />
              </span>
              <span style={{ fontWeight: 700, color: "var(--svc-text-main)" }}>Click here to attach file(s)</span>
              <span style={{ fontSize: "0.85rem", color: "var(--svc-text-muted)" }}>PDF, Excel, Word, or ODT — select multiple</span>
            </label>
          </div>

          {(state.resourceFiles || []).length > 0 && (
            <div style={{ marginTop: "1rem", display: "flex", flexDirection: "column", gap: "6px", textAlign: "left" }}>
              {state.resourceFiles.map((file, idx) => (
                <div key={`${file.name}-${idx}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#ecfdf5", border: "1px solid #6ee7b7", borderRadius: "8px", padding: "8px 12px", fontSize: "0.85rem" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: "8px", overflow: "hidden" }}>
                    <FaFileAlt size={13} color="#059669" />
                    <span style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</span>
                    <span style={{ background: "#d1fae5", color: "#065f46", fontSize: "0.7rem", fontWeight: 700, padding: "2px 6px", borderRadius: "4px", letterSpacing: "0.02em" }}>
                      {badgeLabel(docTypeFromFile(file))}
                    </span>
                    <span style={{ color: "#6b7280", fontSize: "0.75rem" }}>{formatBytes(file.size)}</span>
                  </span>
                  <button type="button" onClick={() => { const next = [...state.resourceFiles]; next.splice(idx, 1); updateState("resourceFiles", next); }} style={{ background: "transparent", border: "none", cursor: "pointer", color: "#ef4444", display: "flex" }} aria-label={`Remove ${file.name}`}>
                    <FaTrash size={12} />
                  </button>
                </div>
              ))}
              <button type="button" className="conv-confirm-btn" style={{ marginTop: "0.5rem" }} onClick={() => { setPhase("review"); ask("review"); }}>
                Continue with {state.resourceFiles.length} file{state.resourceFiles.length !== 1 ? "s" : ""} →
              </button>
            </div>
          )}
        </div>
      );
    }

    if (["serviceName", "amount", "description"].includes(phase)) {
      if (phase === "description") {
        return (
          <div className="conv-widget" style={{ width: "100%" }}>
            <div style={{ position: "relative" }}>
              <textarea className="conv-textarea" rows={5} maxLength={300} placeholder="Describe what they get…" value={textVal} onChange={(e) => setTextVal(e.target.value)} ref={inputRef} />
              <span className={`conv-char-count ${textVal.length > 270 ? "warn" : ""}`}>{300 - textVal.length} left</span>
            </div>
            <button type="button" className="conv-confirm-btn" onClick={handleDescription}>Continue →</button>
          </div>
        );
      }
      const isAmount = phase === "amount";
      return (
        <form className="conv-text-row" onSubmit={handleTextSubmit}>
          {isAmount && <span className="conv-rupee">₹</span>}
          <div className="conv-input-wrap">
            <input ref={inputRef} className="conv-text-input" type={isAmount ? "number" : "text"} placeholder={isAmount ? "Enter amount…" : "Type your answer…"} value={textVal} onChange={(e) => setTextVal(e.target.value)} autoFocus />
          </div>
          <button className="conv-send-btn" type="submit"><FaPaperPlane size={14} /></button>
        </form>
      );
    }
    return null;
  };

  return (
    <div className="scf-root">
      <div className="scf-header">
        <button type="button" className="scf-back" onClick={() => { updateState("menuType", ""); updateState("serviceId", null); }}>
          <IoMdArrowRoundBack size={18} />
        </button>
        <div className="scf-header-text">
          <h2>{t("New Resource")}</h2>
          <p>{t("Follow the steps below to upload your resource")}</p>
        </div>
      </div>

      <div className="scf-divider" />

      <div className="conv-thread">
        {messages.map((msg) => (
          <div key={msg.id} className={`conv-msg-row ${msg.from === "user" ? "conv-msg-row--user" : "conv-msg-row--bot"}`}>
            {msg.from === "bot" && <span className="conv-bot-avatar"><BsStars size={13} /></span>}
            <div className={`conv-bubble ${msg.from === "user" ? "conv-bubble--user" : "conv-bubble--bot"}`}>{msg.text}</div>
          </div>
        ))}

        {typing && (
          <div className="conv-msg-row conv-msg-row--bot">
            <span className="conv-bot-avatar"><BsStars size={13} /></span>
            <div className="conv-bubble conv-bubble--bot conv-typing"><span /><span /><span /></div>
          </div>
        )}

        {phase === "review" && !typing && renderReview()}
        <div ref={bottomRef} />
      </div>

      {phase !== "review" && phase !== "idle" && <div className="conv-dock">{renderInput()}</div>}
    </div>
  );
}

export default function ResourceCreateForm(props) {
  if (props.state.isEdit) return <EditForm {...props} />;
  return <CreateForm {...props} />;
}